import convertDateTimeToString from './convertDateTimeToString';
import createTxpDataRow from './createTxpDataRow';
import populateDiningBusStopData from './populateDiningBusStopData';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import CardItem from '../../schema/viewSchema/CardItem';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import FoodEstablishmentReservation from '../../schema/FoodEstablishmentReservation';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpStrings from '../../schema/configSchema/TxpStrings';
import TxpDateTimeCallbacks from '../../schema/configSchema/TxpDateTimeCallbacks';

/**
 *  Extracts the dining specific data needed for displaying the card
 */
export default function createDiningCard(
    diningEntity: FoodEstablishmentReservation,
    isBusStop: boolean,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons,
    txpDateTimeCallbacks?: TxpDateTimeCallbacks
): CardItem {
    let restaurantInfo = diningEntity.restaurantInfo;
    let summaryFirstLine = txpStrings.formatters.format(
        txpStrings.strings.reservationText,
        restaurantInfo.name
    );
    let summarySecondLine =
        diningEntity.reservationId &&
        txpStrings.formatters.format(
            txpStrings.strings.reservationCodeText,
            diningEntity.reservationId
        );

    let organizerName = diningEntity.underName.name;

    let startTime = convertDateTimeToString(
        diningEntity.startTime,
        false,
        txpStrings.formatters,
        txpDateTimeCallbacks
    );
    let ariaStartTime = convertDateTimeToString(
        diningEntity.startTime,
        true,
        txpStrings.formatters,
        txpDateTimeCallbacks
    );
    let attendeesText = getAttendeesText(organizerName, diningEntity.partySize, txpStrings);
    let telephone = restaurantInfo.telephone ? restaurantInfo.telephone : null;

    let locationText = restaurantInfo.address ? restaurantInfo.address.streetAddress : null;

    const actionButtonData = diningEntity.modifyReservationUrl
        ? {
              actionIconName: detailsIcons.edit,
              actionUrl: diningEntity.modifyReservationUrl,
              actionButtonText: txpStrings.strings.modifyReservationText,
              actionName: ActionNameConstants.ModifyReservation,
          }
        : null;

    let busStopData: BusStopData;
    if (isBusStop) {
        busStopData = populateDiningBusStopData(
            restaurantInfo,
            startTime,
            ariaStartTime,
            txpStrings,
            detailsIcons.eatDrink
        );
    }

    let ariaText: string = txpStrings.formatters.format(
        txpStrings.strings.restaurantAriaFormatText,
        ariaStartTime,
        organizerName,
        telephone,
        locationText
    );

    locationText = showLocation ? locationText : null;

    return {
        id: activityId,
        entityIcon: entityIcon,
        summaryData: {
            firstLine: { text: summaryFirstLine },
            secondLine: { text: summarySecondLine },
        },
        entityImage: entityImage,
        activityType: ActivityType.Dining,
        entityType: EntityType.FoodReservation,
        detailsData: {
            firstLine: createTxpDataRow(
                startTime,
                txpStrings.strings.eventTimePrefixText,
                detailsIcons.clock
            ),
            secondLine: createTxpDataRow(
                attendeesText,
                txpStrings.strings.reservationForText,
                detailsIcons.contact
            ),
            thirdLine: createTxpDataRow(
                telephone,
                txpStrings.strings.restaurantReservationPhoneText,
                detailsIcons.phone
            ),
            fourthLine: createTxpDataRow(
                locationText,
                null,
                detailsIcons.location,
                false /* is required */,
                null /* placeholder */,
                true /* includeToolTip */
            ),
        },
        busStopData: busStopData,
        brandsSummaryFirstLine: restaurantInfo.name,
        brandsSummarySecondLine: txpStrings.formatters.formatWeekDayDateTime(
            diningEntity.startTime
        ),
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
        ariaText: ariaText,
    } as CardItem;
}

/**
 * Computes the attendees text to be shown based on the partysize
 */
function getAttendeesText(personName: string, partySize: number, txpStrings: TxpStrings): string {
    let attendeesText = personName;
    if (partySize == 1) {
        attendeesText = txpStrings.formatters.format(
            txpStrings.strings.restaurantReservationWithOneGuestText,
            personName
        );
    } else if (partySize > 1) {
        attendeesText = txpStrings.formatters.format(
            txpStrings.strings.restaurantReservationWithGuestsText,
            personName,
            partySize
        );
    }
    return attendeesText;
}
